// @ts-nocheck
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

export const ISSUE_CATEGORIES = [
  'Bug',
  'Security',
  'College Coach',
  'Athlete',
  'Club Coach',
  'Parent',
  'Events',
  'Other',
] as const;

export type IssueCategory = typeof ISSUE_CATEGORIES[number];

export interface AdminIssue {
  id: string;
  title: string;
  description: string | null;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: string | null;
  created_by: string;
  assigned_to: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  display_order: number;
  creator?: {
    id: string;
    name: string | null;
    email: string | null;
    avatar_url: string | null;
  };
  assignee?: {
    id: string;
    name: string | null;
    email: string | null;
    avatar_url: string | null;
  };
  comment_count?: number;
}

export interface IssueComment {
  id: string;
  issue_id: string;
  user_id: string;
  content: string;
  created_at: string;
  updated_at: string;
  user?: {
    id: string;
    name: string | null;
    email: string | null;
    avatar_url: string | null;
  };
}

export function useAdminIssues() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const issuesQuery = useQuery({
    queryKey: ['admin-issues'],
    queryFn: async () => {
      const { data: issues, error } = await supabase
        .from('admin_issues')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;

      // Fetch profiles for creators and assignees
      const userIds = new Set<string>();
      issues?.forEach(issue => {
        userIds.add(issue.created_by);
        if (issue.assigned_to) userIds.add(issue.assigned_to);
      });

      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, name, email, avatar_url')
        .in('user_id', Array.from(userIds));

      const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);

      // Fetch comment counts
      const { data: commentCounts } = await supabase
        .from('admin_issue_comments')
        .select('issue_id');

      const countMap = new Map<string, number>();
      commentCounts?.forEach(c => {
        countMap.set(c.issue_id, (countMap.get(c.issue_id) || 0) + 1);
      });

      return issues?.map(issue => ({
        ...issue,
        creator: profileMap.get(issue.created_by),
        assignee: issue.assigned_to ? profileMap.get(issue.assigned_to) : undefined,
        comment_count: countMap.get(issue.id) || 0,
      })) as AdminIssue[];
    },
    enabled: !!user,
  });

  const createIssueMutation = useMutation({
    mutationFn: async (data: { 
      title: string; 
      description?: string; 
      priority?: 'low' | 'medium' | 'high' | 'urgent';
      category?: string;
      assigned_to?: string;
    }) => {
      // Get max display_order
      const { data: maxOrder } = await supabase
        .from('admin_issues')
        .select('display_order')
        .order('display_order', { ascending: false })
        .limit(1)
        .maybeSingle();

      const newOrder = (maxOrder?.display_order || 0) + 1;

      const { data: issue, error } = await supabase
        .from('admin_issues')
        .insert({
          title: data.title,
          description: data.description || null,
          priority: data.priority || 'medium',
          category: data.category || null,
          assigned_to: data.assigned_to || null,
          created_by: user?.id,
          display_order: newOrder,
        })
        .select()
        .single();

      if (error) throw error;
      
      // Return both issue and input data for notification
      return { issue, inputData: data };
    },
    onSuccess: async ({ issue, inputData }) => {
      queryClient.invalidateQueries({ queryKey: ['admin-issues'] });
      toast.success('Issue created');

      // If assigned to someone (not the creator), send notification
      if (inputData.assigned_to && inputData.assigned_to !== user?.id) {
        // Get creator's name
        const { data: creatorProfile } = await supabase
          .from('profiles')
          .select('name, email')
          .eq('user_id', user?.id)
          .maybeSingle();

        const creatorName = creatorProfile?.name || creatorProfile?.email || 'Admin';

        // Fire and forget notification
        invokeEdgeFunction('notify-issue-assignment', {
          body: {
            issue_id: issue.id,
            issue_title: issue.title,
            issue_description: issue.description,
            assignee_id: inputData.assigned_to,
            creator_name: creatorName,
            priority: issue.priority,
            category: issue.category,
          },
        }).then(res => {
          if (res.error) console.error('Failed to send assignment notification:', res.error);
          else console.log('Assignment notification sent:', res.data);
        });
      }
    },
    onError: (error) => {
      toast.error('Failed to create issue: ' + error.message);
    },
  });

  const getCurrentAdminName = async () => {
    if (!user?.id) return 'Admin';

    const { data: profile } = await supabase
      .from('profiles')
      .select('name, email')
      .eq('user_id', user.id)
      .maybeSingle();

    return profile?.name || profile?.email || 'Admin';
  };

  const updateIssueMutation = useMutation({
    mutationFn: async (data: { 
      id: string; 
      title?: string; 
      description?: string; 
      status?: 'open' | 'in_progress' | 'resolved' | 'closed';
      priority?: 'low' | 'medium' | 'high' | 'urgent';
      category?: string;
      assigned_to?: string | null;
      previous_assigned_to?: string | null;
    }) => {
      const updates: Record<string, unknown> = {};
      if (data.title !== undefined) updates.title = data.title;
      if (data.description !== undefined) updates.description = data.description;
      if (data.status !== undefined) {
        updates.status = data.status;
        if (data.status === 'resolved' || data.status === 'closed') {
          updates.resolved_at = new Date().toISOString();
        }
      }
      if (data.priority !== undefined) updates.priority = data.priority;
      if (data.category !== undefined) updates.category = data.category;
      if (data.assigned_to !== undefined) updates.assigned_to = data.assigned_to;

      const { data: issue, error } = await supabase
        .from('admin_issues')
        .update(updates)
        .eq('id', data.id)
        .select()
        .single();

      if (error) throw error;
      return issue;
    },
    onSuccess: async (issue, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-issues'] });
      toast.success('Issue updated');

      // Notify on (re)assignment when assigned_to changes and it's not a self-assign
      const newAssignee = variables.assigned_to;
      const prevAssignee = variables.previous_assigned_to;
      const changedAssignee = variables.assigned_to !== undefined && newAssignee !== prevAssignee;

      if (changedAssignee && newAssignee && newAssignee !== user?.id) {
        const creatorName = await getCurrentAdminName();

        supabase.functions
          .invoke('notify-issue-assignment', {
            body: {
              issue_id: issue.id,
              issue_title: issue.title,
              issue_description: issue.description,
              assignee_id: newAssignee,
              creator_name: creatorName,
              priority: issue.priority,
              category: issue.category,
            },
          })
          .then((res) => {
            if (res.error) console.error('Failed to send assignment notification:', res.error);
            else console.log('Assignment notification sent:', res.data);
          });
      }
    },
    onError: (error) => {
      toast.error('Failed to update issue: ' + error.message);
    },
  });

  const deleteIssueMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('admin_issues')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-issues'] });
      toast.success('Issue deleted');
    },
    onError: (error) => {
      toast.error('Failed to delete issue: ' + error.message);
    },
  });

  const reorderIssuesMutation = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      // Update display_order for each issue
      const updates = orderedIds.map((id, index) => 
        supabase
          .from('admin_issues')
          .update({ display_order: index })
          .eq('id', id)
      );
      
      await Promise.all(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-issues'] });
    },
    onError: (error) => {
      toast.error('Failed to reorder issues: ' + error.message);
      queryClient.invalidateQueries({ queryKey: ['admin-issues'] });
    },
  });

  return {
    issues: issuesQuery.data || [],
    isLoading: issuesQuery.isLoading,
    createIssue: createIssueMutation.mutateAsync,
    updateIssue: updateIssueMutation.mutateAsync,
    deleteIssue: deleteIssueMutation.mutateAsync,
    reorderIssues: reorderIssuesMutation.mutateAsync,
    isCreating: createIssueMutation.isPending,
    isUpdating: updateIssueMutation.isPending,
    isReordering: reorderIssuesMutation.isPending,
  };
}

export function useIssueComments(issueId: string | null, issueTitle?: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const commentsQuery = useQuery({
    queryKey: ['issue-comments', issueId],
    queryFn: async () => {
      if (!issueId) return [];

      const { data: comments, error } = await supabase
        .from('admin_issue_comments')
        .select('*')
        .eq('issue_id', issueId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const userIds = Array.from(new Set((comments || []).map((c) => c.user_id)));
      if (userIds.length === 0) return (comments || []) as IssueComment[];

      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, name, email, avatar_url')
        .in('user_id', userIds);

      if (profilesError) throw profilesError;

      const profileMap = new Map(profiles?.map((p) => [p.user_id, p]) || []);

      return (comments || []).map((comment) => ({
        ...comment,
        user: profileMap.get(comment.user_id),
      })) as IssueComment[];
    },
    // Don't gate fetching comments on the client-side user object; RLS will enforce access.
    enabled: !!issueId,
  });

  const addCommentMutation = useMutation({
    mutationFn: async (content: string) => {
      if (!issueId) throw new Error('No issue selected');
      if (!user?.id) throw new Error('You must be logged in to comment');

      const { data, error } = await supabase
        .from('admin_issue_comments')
        .insert({
          issue_id: issueId,
          user_id: user.id,
          content,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: async (created, content) => {
      // Optimistically update the comments list so it appears immediately.
      const { data: profile } = await supabase
        .from('profiles')
        .select('user_id, name, email, avatar_url')
        .eq('user_id', created.user_id)
        .maybeSingle();

      queryClient.setQueryData<IssueComment[]>(['issue-comments', issueId], (prev) => {
        const safePrev = prev || [];
        if (safePrev.some((c) => c.id === created.id)) return safePrev;
        return [...safePrev, { ...created, user: profile || undefined } as IssueComment];
      });

      queryClient.invalidateQueries({ queryKey: ['admin-issues'] });
      toast.success('Comment added');

      // Send email notification to other admins (fire and forget)
      if (issueTitle) {
        invokeEdgeFunction('notify-issue-comment', {
          body: {
            issue_id: issueId,
            issue_title: issueTitle,
            comment_content: content,
            commenter_name: profile?.name || profile?.email || 'Admin',
            commenter_id: user?.id,
          },
        }).then(res => {
          if (res.error) console.error('Failed to send comment notification:', res.error);
          else console.log('Comment notification sent:', res.data);
        });
      }
    },
    onError: (error) => {
      toast.error('Failed to add comment: ' + error.message);
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: async (commentId: string) => {
      const { error } = await supabase
        .from('admin_issue_comments')
        .delete()
        .eq('id', commentId);

      if (error) throw error;
    },
    onSuccess: (_data, commentId) => {
      queryClient.setQueryData<IssueComment[]>(['issue-comments', issueId], (prev) =>
        (prev || []).filter((c) => c.id !== commentId)
      );
      queryClient.invalidateQueries({ queryKey: ['admin-issues'] });
    },
    onError: (error) => {
      toast.error('Failed to delete comment: ' + error.message);
    },
  });

  return {
    comments: commentsQuery.data || [],
    isLoading: commentsQuery.isLoading,
    isError: commentsQuery.isError,
    error: commentsQuery.error,
    refetch: commentsQuery.refetch,
    addComment: addCommentMutation.mutateAsync,
    deleteComment: deleteCommentMutation.mutateAsync,
    isAddingComment: addCommentMutation.isPending,
  };
}
